import { Dialog as RadixDialog } from 'radix-ui';
import type { ReactNode } from 'react';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  /** Renders a top-right "X" close control. On by default per ui-guidelines.md §10 ("Standard modals: X close button top-right"); destructive-only dialogs should pass `false` explicitly. */
  showCloseButton?: boolean;
  /** Max width. `md` (default, 448px) for confirm/form dialogs; `lg`/`xl` for wider content (e.g. the share dialog's side-by-side input + button); `2xl` for two-pane content (e.g. the resource detail dialog's file list + preview). */
  size?: 'md' | 'lg' | 'xl' | '2xl';
  /** `left` (default) for form/confirm dialogs; `center` for hero-style dialogs (icon + centered heading, e.g. the browse-public-pages popup). */
  align?: 'left' | 'center';
  /** Icon rendered centered above the title (hero dialogs). Pair with `align="center"`. */
  headerIcon?: ReactNode;
  /** Override the title's typography (e.g. a larger hero heading). */
  titleClassName?: string;
}

const SIZE_CLASS: Record<NonNullable<DialogProps['size']>, string> = {
  md: 'max-w-md',
  lg: 'max-w-xl',
  xl: 'max-w-2xl',
  '2xl': 'max-w-4xl',
};

/**
 * The single generic modal primitive (mnr-standards: "exactly one Dialog in
 * ui/, built on Radix Dialog"). Every feature dialog (confirmations, forms in
 * a modal) composes this rather than re-implementing overlay/focus-trap/Esc.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  showCloseButton = true,
  size = 'md',
  align = 'left',
  headerIcon,
  titleClassName,
}: DialogProps) {
  const centered = align === 'center';
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-navy/80" />
        <RadixDialog.Content
          className={`fixed left-1/2 top-1/2 z-50 w-full ${SIZE_CLASS[size]} -translate-x-1/2 -translate-y-1/2 rounded-modal bg-surface-overlay p-8 shadow-lg focus:outline-none`}
        >
          {showCloseButton && (
            <RadixDialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="absolute right-4 top-4 cursor-pointer rounded-lg p-1 text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </RadixDialog.Close>
          )}
          {headerIcon && <div className="mb-4 flex justify-center">{headerIcon}</div>}
          {/* ui-guidelines.md §10: every modal heading is H5, centered, navy -
              unconditionally, not opt-in via `align`. `titleClassName` remains
              as an explicit override for hero-style dialogs. */}
          <RadixDialog.Title className={titleClassName ?? 'text-h5 text-text-primary text-center'}>
            {title}
          </RadixDialog.Title>
          {description && (
            <RadixDialog.Description className={`mt-2 text-sm text-subtext ${centered ? 'text-center' : ''}`.trim()}>
              {description}
            </RadixDialog.Description>
          )}
          {children && <div className="mt-4">{children}</div>}
          {/* ui-guidelines.md §10: "Buttons: stacked full-width, primary on
              top." Plain flex-col (not reversed) so visual order matches DOM
              order (first child on top) - keyboard tab order and screen
              reader reading order then match what's seen, so callers put the
              primary/confirm action first. `[&>*]:w-full` stretches every
              direct child (each Button, or a single non-Button footer node)
              without requiring every caller to add `w-full` itself. */}
          {footer && <div className="mt-6 flex flex-col gap-3 [&>*]:w-full">{footer}</div>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
